import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef, ElementRef, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgForm, FormBuilder } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { ViewTableComponent } from 'src/app/components/base/view-table/view-table.component';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MY_DATE_FORMATS } from 'src/app/components/reports/payments-report-view/payments-report-view.component';
import { PaymentReceiptComponent } from 'src/app/components/payments/payment-receipt/payment-receipt.component';
import { DialogService } from 'src/app/services/dialog.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { UtilityService } from 'src/app/services/utility.service';
import { FieldsService } from 'src/app/services/fields.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { RouteEventsService } from 'src/app/route-events.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { PackagesService } from 'src/app/services/packages.service';
import { UserService } from 'src/app/services/user.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { TranslateService } from '@ngx-translate/core';
import { StudioTaxService, StudioTaxMode } from 'src/app/services/studio-tax.service';

@Component({
  selector: 'app-payment-reports',
  templateUrl: './payment-reports.component.html',
  styleUrls: ['./payment-reports.component.css'],
  providers: [
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: MY_DATE_FORMATS }
  ]
})

export class PaymentReportsComponent extends ViewTableComponent implements OnInit, OnDestroy {

  @Input() drupal_student_id: number;
  @Input() drupal_student_name: string;
  @Input() drupal_account_id: string;
  @Output() dataRefreshed = new EventEmitter<void>();

  hasTaxData: boolean = false;

  constructor(
    refreshService: RefreshService,
    dialogService: DialogService,
    entityRESTService: EntityRESTService,
    utilityService: UtilityService,
    fieldsService: FieldsService,
    drupalRESTService: DrupalRESTService,
    taxonomyService: TaxonomyService,
    router: Router,
    activatedRoute: ActivatedRoute,
    authRESTService: AuthRESTService,
    authService: AuthService,
    menuRESTService: MenuRESTService,
    formBuilder: FormBuilder,
    routeEventsService: RouteEventsService,
    permissionsService: PermissionsService,
    renderer2: Renderer2,
    configPages: ConfigPagesService,
    packagesService: PackagesService,
    snackBar: MatSnackBar,
    userRESTService: UserService,
    el: ElementRef,
    changeDetectorRef: ChangeDetectorRef,
    sanitizer: DomSanitizer,
    studioConfigService: StudioConfigService,
    componentRefreshService: ComponentRefreshService,
    translateService: TranslateService,
    private dialog: MatDialog,
    private studioTaxService: StudioTaxService
  ) {
    super(
      refreshService,
      dialogService,
      entityRESTService,
      utilityService,
      fieldsService,
      drupalRESTService,
      taxonomyService,
      router,
      activatedRoute,
      authRESTService,
      authService,
      menuRESTService,
      formBuilder,
      routeEventsService,
      permissionsService,
      renderer2,
      configPages,
      packagesService,
      snackBar,
      userRESTService,
      el,
      changeDetectorRef,
      sanitizer,
      studioConfigService,
      componentRefreshService,
      translateService
    );
    this.taxModeSubscription = this.studioTaxService.getTaxMode().subscribe(mode => {
      this.taxMode = mode;
    });
  }

  // getTaxMode() is backed by a long-lived BehaviorSubject; an open subscription
  // would keep this component alive after the dashboard closes.
  private taxModeSubscription: Subscription;

  override ngOnDestroy(): void {
    this.taxModeSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  override displayedColumns: string[] = [
    'field_student_name',
    'field_payment_id',
    'field_date_and_time',
    'field_gross_tuition',
    'field_enrollment_name',
    'field_payment_type',
    'field_teacher',
    'edit',
  ];

  /** Studio tax mode; drives whether the Total adds tax or already contains it. */
  private taxMode: StudioTaxMode | null = null;

  /**
   * The customer-facing total for one payment row: the payment's own stored
   * field_total_payment_amount, written mode-correctly at entry time (gross +
   * tax for exclusive-tax studios; just gross when the tax was inside the
   * amount the customer handed over: a $676.20 inclusive payment is $676.20,
   * not $769.73). Legacy payments predating that field fall back to a
   * computed total gated by the studio's current tax mode.
   * The view renders field_gross_tuition pre-formatted ("$1,100.00") and the
   * other figures raw, so strip everything but digits/sign/decimal point.
   */
  paymentTotal(row: any): number {
    const num = (v: any) => parseFloat(String(v ?? '').replace(/[^0-9.\-]/g, '')) || 0;
    const stored = num(row?.field_total_payment_amount);
    if (stored > 0) {
      return stored;
    }
    const gross = num(row?.field_gross_tuition);
    if (this.taxMode?.hasInclusiveTax && !this.taxMode?.hasExclusiveTax) {
      return gross;
    }
    return gross + num(row?.field_tax_collected);
  }

  override getData(params: { parameter: string; value: string; }[]) {
    this.ShowProgressBar = true;
    this.queryParams = params;

    if (this.sortParams !== undefined) {
      this.queryParams.push(this.sortParams)
    }

    this._utilityService.getPaymentReportStudent(
      this.drupal_student_id,
      this.queryParams
    )
      .subscribe((data) => {
        this.ShowProgressBar = false;
        this.data = data;
        this.dataSource = new MatTableDataSource(data['results']);

        // Check if any of the data has a non-zero or non-empty field_tax_amount
        this.hasTaxData = data && data['results'] && data['results'].some(
          (item: any) =>
            item.field_tax_collected !== '' &&
            item.field_tax_collected !== '0' &&
            item.field_tax_collected !== '0.00'
        );

        // If tax data exists, insert the "Tax Amount" column after "Gross Tuition"
        const taxColumnIndex = this.displayedColumns.indexOf('field_gross_tuition') + 1;
        if (this.hasTaxData && !this.displayedColumns.includes('field_tax_collected')) {
          this.displayedColumns.splice(taxColumnIndex, 0, 'field_tax_collected');
        }
        // ...and a Total (gross + tax) column after it, so each payment shows what
        // the customer actually handed over, the figure iDance/receipts show.
        if (this.hasTaxData && !this.displayedColumns.includes('payment_total')) {
          this.displayedColumns.splice(this.displayedColumns.indexOf('field_tax_collected') + 1, 0, 'payment_total');
        }


        // Pagination
        if (data['results']) {
          this.setupPagination(data, params);
        }
      },
        error => this.handleError(error)
      )
  }

  // Override the openEntityComponent to emit event after payment dialog closes
  override openEntityComponent(component: any, eckType: any, bundle: any, action: any, EntityID?: any, fieldsData?: {}) {
    this._dialogService.openDialog(component, "defaultWithData", {
      data: {
        EntityID: EntityID,
        eckType: eckType,
        bundle: bundle,
        action: action,
        fieldsData: fieldsData ?? '',
      },
    }).afterClosed().subscribe(data => {
      // Refresh the payment data
      this.getData(this.queryParams);
      // Emit event to notify parent component
      this.dataRefreshed.emit();
    });
  }

  openReceiptDialog(paymentId: number): void {
    this.dialog.open(PaymentReceiptComponent, {
      width: '800px',
      height: '800px',
      data: { paymentId: paymentId },
      panelClass: 'receipt-dialog',
      disableClose: false
    });
  }
}
